// Reserved for future assistant services
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";

import * as Sentry from "@sentry/react";
import axios from "axios";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { nuclearn_api_url } from "src/App";
import { shouldNotRetry404 } from "src/config/react-query";
import { authorizedUser } from "src/helpers/Auth";
import { resolveContentType } from "src/reusable/Chat/ChatStreaming/services/contentTypeUtils";
import {
  canMergeConsecutiveParts,
  normalizeContentParts,
} from "src/reusable/Chat/ChatStreaming/services/messageParserUtils";

// Non-array 2xx bodies (e.g. an auth redirect serving HTML) crash list consumers
// at `.filter`/`.map`. Degrade to [] and report it. See FRONTEND-W3.
const ensureArray = (data, label) => {
  if (Array.isArray(data)) return data;
  if (Sentry.getClient()) {
    Sentry.captureMessage(
      `${label} returned a non-array body (got ${typeof data})`,
      "warning",
    );
  }
  return [];
};

const getDialoguesById = (dialogueId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue`, {
      params: {
        dialogue_id: dialogueId,
      },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetDialoguesById = (dialogueId) => {
  return useQuery(
    ["dialogues", dialogueId],
    () => getDialoguesById(dialogueId),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(dialogueId),
    },
  );
};

const getDialogueSummaryById = (dialogueId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue/${dialogueId}/summary`)
    .then((response) => {
      return response.data;
    });
};

export const useGetDialogueSummaryById = (dialogueId) => {
  return useQuery(
    ["dialogueSummary", dialogueId],
    () => getDialogueSummaryById(dialogueId),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(dialogueId),
    },
  );
};

export const _chatConverseWithSearch = (
  modelId,
  modelVersionNum,
  inferData,
  datasetsForSearchContext,
  dialogueId,
  n_search_results,
) => {
  console.log("Infer from model");
  console.log(modelId, modelVersionNum, inferData, datasetsForSearchContext);
  let dataset_fks = datasetsForSearchContext.map((dataset) => dataset.id);

  let postData = {
    model_inference_data: inferData,
    search_datasets: dataset_fks,
    n_search_results: n_search_results,
    model_id: modelId,
    version_number: modelVersionNum,
    search_enhanced: false,
    dialogue_id: dialogueId,
  };

  const searchUrl = `${nuclearn_api_url}/chat/converse`;

  return axios
    .post(searchUrl, postData)
    .then((response) => {
      console.log("Conversation response data:", response.data);
      return response.data;
    })
    .catch((error) => {
      console.log("Error in conversation response data:", error);
      // if the status code is 400, it means the request has timed out and we probably threw this error intentionally
      if (error.response.status === 400 || error.response.status === 524) {
        console.log("Request timed out");
        throw error;
      }
    });
};

export const _chatConverse = (
  modelId,
  modelVersionNum,
  inferData,
  dialogueId,
) => {
  // Run Inference against a model route
  let postData = {
    model_id: modelId,
    version_number: modelVersionNum,
    model_inference_data: inferData,
    dialogue_id: dialogueId,
    feedback_type_id: 2,
  };
  console.log("postdata", postData);
  return axios
    .post(
      `${nuclearn_api_url}/chat/converse?model_id=${modelId}&version_number=${modelVersionNum}&dataset_id=${2}&dialogue_id=${1}&search_enhanced=${false}`,
      postData,
    )
    .then((response) => {
      console.log("Conversation response data:", response.data);
      return response.data;
    })
    .catch((error) => {
      console.log("Error in conversation response data:", error);
      throw error;
    });
};

export const useChatConverse = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      model_id: modelId,
      model_version: modelVersionNum,
      infer_data: inferData,
      datasetsForSearchContext,
      n_search_results,
      dialogueId,
      // eslint-disable-next-line no-unused-vars
      userMessage = null,
      // eslint-disable-next-line no-unused-vars
      messageIndex = null,
      // eslint-disable-next-line no-unused-vars
      regen = false,
    }) => {
      let validDialogueId = null;
      if (dialogueId === -1) {
        validDialogueId = null;
      } else {
        validDialogueId = dialogueId;
      }

      if (datasetsForSearchContext && datasetsForSearchContext.length > 0) {
        return _chatConverseWithSearch(
          modelId,
          modelVersionNum,
          inferData,
          datasetsForSearchContext,
          validDialogueId,
          n_search_results,
        );
      }
      return _chatConverse(
        modelId,
        modelVersionNum,
        inferData,
        validDialogueId,
      );
    },
    onSuccess: (data, variables) => {
      // console.log("on success data call", data);
      const {
        dialogue_id: dialogueId,
        // eslint-disable-next-line no-unused-vars
        inference_output: inferenceOutput,
        // eslint-disable-next-line no-unused-vars
        platform_control_signals: platformControlSignals,
      } = variables;
      queryClient.invalidateQueries(["dialogues", dialogueId]);
      onSuccess(data, variables);
    },
    onError: (error) => {
      console.log(error.response);
      onError(error);
    },
  });
};

const getDialogueById = (dialogueId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue`, {
      params: {
        dialogue_id: dialogueId,
      },
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetDialogueById = ({ dialogueId }) => {
  return useQuery(["dialogue", dialogueId], () => getDialogueById(dialogueId), {
    refetchOnWindowFocus: false,
    enabled: Boolean(dialogueId),
    // A 404 means the dialogue is missing or not the caller's (backend returns
    // the same 404 for both — #30212); deterministic, so fail fast to the
    // access banner instead of retrying behind backoff.
    retry: shouldNotRetry404,
    select: (data) => {
      if (!data) return null;

      // Transform the dialogue data
      const parsedMessages = data.messages ? JSON.parse(data.messages) : [];

      // Step 1: Build contentParts for each message
      const messagesWithParts = parsedMessages.map((message) => {
        const contentObj =
          typeof message.content === "object"
            ? message.content
            : { text: message.content };

        // Preserve images from the message content for VLM display
        const images = contentObj.images || undefined;

        // Prefer structured content_parts if present (new protocol data)
        if (
          contentObj.content_parts &&
          Array.isArray(contentObj.content_parts) &&
          contentObj.content_parts.length > 0
        ) {
          const normalizedContentParts = contentObj.content_parts
            .map((part) => {
              const text =
                typeof part?.text === "string"
                  ? part.text
                  : String(part?.text ?? "");
              const resolvedType = resolveContentType(part?.type, text);
              const fallbackType =
                message.type === "Tool" ? "tool_response" : "text";
              return {
                type: resolvedType || fallbackType,
                text,
              };
            })
            .filter(
              (part) =>
                part.text !== "" ||
                part.type === "tool_call" ||
                part.type === "tool_response",
            );

          return {
            ...message,
            contentParts: normalizeContentParts(normalizedContentParts),
            images,
          };
        }

        const contentString = contentObj.text || "";

        // Some backend paths emit Tool utterances without content_type. Keep
        // them in the tool lane to avoid raw JSON rendering as assistant text.
        if (message.type === "Tool") {
          return {
            ...message,
            contentParts: contentString
              ? [{ type: "tool_response", text: contentString }]
              : [],
            images,
          };
        }

        // Legacy fallback: wrap plain text
        return {
          ...message,
          contentParts: normalizeContentParts([
            { type: "text", text: contentString },
          ]),
          images,
        };
      });

      // Step 2: Merge consecutive messages with the same ID into one
      // message with combined contentParts. During streaming, the event
      // handler merges same-ID chunks (text, tool_call, tool_response)
      // into one message. We replicate that here for DB-loaded history.
      const dialogueWithParts = [];
      for (const msg of messagesWithParts) {
        const prev = dialogueWithParts[dialogueWithParts.length - 1];
        if (prev && prev.id && prev.id === msg.id) {
          // Merge contentParts — concat same-part fragments,
          // push distinct parts (incl. parallel tool siblings) as new entries
          for (const part of msg.contentParts || []) {
            const lastPart = prev.contentParts[prev.contentParts.length - 1];
            if (lastPart && canMergeConsecutiveParts(lastPart, part)) {
              lastPart.text = (lastPart.text || "") + (part.text || "");
            } else {
              prev.contentParts.push(part);
            }
          }
        } else {
          dialogueWithParts.push(msg);
        }
      }

      return {
        ...data,
        messages: dialogueWithParts,
        temperature: data.config?.temperature,
        toolCalling: data.config?.tool_calling,
        datasetIds: data.config?.dataset_ids ?? [],
      };
    },
  });
};

const setDialogueShared = (dialogueId, isShared) => {
  return axios
    .patch(`${nuclearn_api_url}/chat/dialogue/${dialogueId}/share`, {
      is_shared: isShared,
    })
    .then((response) => response.data);
};

// Toggle opt-in link sharing on a dialogue. On success, refresh the cached
// dialogue so the Share button reflects the new is_shared state immediately.
export const useShareDialogue = ({ options } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      /** @type {{ dialogueId: number; isShared: boolean }} */
      { dialogueId, isShared },
    ) => setDialogueShared(dialogueId, isShared),
    onSuccess: (_data, { dialogueId }) => {
      queryClient.invalidateQueries(["dialogue", dialogueId]);
      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: options?.onError,
  });
};

const getDialogues = ({
  pageParam = 0,
  searchQuery = "",
  productKey = "",
  // Arbitrary product-specific query params (e.g. { kind: "search" } for Parts
  // AI dialogue-kind scoping, or { location_id } for equipment-AI).
  extraFilters = {},
}) => {
  const limit = 50;
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue`, {
      params: {
        skip: pageParam * limit,
        limit: limit,
        search_query: searchQuery,
        product: productKey,
        ...extraFilters,
      },
    })
    .then((response) => {
      return {
        data: response.data.items,
        nextPage:
          response.data.page < response.data.pages
            ? response.data.page
            : undefined,
        totalPages: response.data.pages,
      };
    });
};

export const useGetAllDialogues = (
  searchQuery = "",
  productKey = "",
  extraFilters = {},
) => {
  return useInfiniteQuery({
    // extraFilters is part of the key so a scoped list (e.g. a Parts AI kind, or
    // an equipment-AI location) doesn't share a cache with the unfiltered list.
    queryKey: ["dialogues", searchQuery, productKey, extraFilters],
    queryFn: ({ pageParam = 0 }) =>
      getDialogues({ pageParam, searchQuery, productKey, extraFilters }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.nextPage) {
        return pages.length;
      }
      return undefined;
    },
  });
};

const updateChatRanking = (
  dialogueId,
  rankingUpdate,
  contextDialogue,
  type,
) => {
  return axios
    .post(
      `${nuclearn_api_url}/chat/update-ranking`,
      {
        dialogue_id: dialogueId,
        ranking_update: rankingUpdate,
        context_dialogue: contextDialogue,
        rank_type: type,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    .then((response) => {
      return response.data;
    });
};

export const useUpdateChatRanking = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const {
        dialogue_id: dialogueId,
        ranking_update,
        context_dialogue,
        type,
      } = vars;
      return updateChatRanking(
        dialogueId,
        ranking_update,
        context_dialogue,
        type,
      );
    },
    {
      onSuccess: () => {
        // queryClient.invalidateQueries("dialogues", dialogueId);
        queryClient.invalidateQueries("feedbackranking");
      },
      onError: (error) => {
        console.error(error.response);
      },
    },
  );
};

const getFeedbackRanking = (dialogueId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/get-feedback-values/${dialogueId}`)
    .then((response) => {
      return response.data;
    });
};

export const useGetFeedbackRanking = (dialogueId) => {
  return useQuery(
    ["feedbackranking", dialogueId],
    () => getFeedbackRanking(dialogueId),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(dialogueId),
    },
  );
};

export const getAllConfigs = ({ searchQuery, orderBy }) => {
  const params = {};
  if (searchQuery) params.search_query = searchQuery;
  if (orderBy) params.order_by = orderBy;

  return axios
    .get(`${nuclearn_api_url}/chat/config`, { params })
    .then((response) => ensureArray(response.data, "GET /chat/config"))
    .catch((error) => {
      throw error;
    });
};

export const useGetAllConfigs = ({ searchQuery, orderBy }) => {
  return useQuery(
    ["atomAssistConfig", searchQuery, orderBy],
    () => getAllConfigs({ searchQuery, orderBy }),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getConfigBySlugName = (slugName) => {
  return axios
    .get(`${nuclearn_api_url}/chat/config/${slugName}`)
    .then((response) => {
      return response.data;
    });
};

export const useGetConfigBySlugName = (slugName) => {
  return useQuery(
    ["atomAssistConfig", slugName],
    () => getConfigBySlugName(slugName),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(slugName),
      staleTime: 1000 * 60 * 20,
      cacheTime: 1000 * 60 * 60,
      // A 404 is the answer (removed, or not shared with this user), not a
      // blip: retrying only delays the not-found notice.
      retry: shouldNotRetry404,
      select: (data) => {
        if (!data) return null;

        // Extract model capabilities from model_version.inference_data_config
        const inferenceConfig = data?.model_version?.inference_data_config;
        const modelCapabilities =
          inferenceConfig && typeof inferenceConfig === "object"
            ? {
                supportsVision: inferenceConfig.supports_vision === true,
                supportsReasoning: inferenceConfig.supports_reasoning === true,
                supportsFunctionCalling:
                  inferenceConfig.supports_function_calling === true,
              }
            : {};

        return {
          name: data.name,
          slug: data.slug,
          description: data.description,
          initialPrompt: data.initial_prompt,
          systemPrompt: data.system_prompt,
          modelId: data?.config_data?.model_id ?? null,
          modelVersionNum: data?.model_version?.version_number ?? null,
          datasets: data?.datasets,
          chatControlMetadata: {},
          topSearchResults: data.config_data?.top_search_results,
          toolCalling: data.config_data?.tool_calling,
          chatConfigId: data.id,
          modelCapabilities,
          visibilityLevel: data.visibility_level,
          myAccess: data.my_access,
        };
      },
    },
  );
};

const getConfigById = (configId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/config/id/${configId}`)
    .then((response) => {
      return response.data;
    });
};

export const useGetConfigById = (configId) => {
  return useQuery(
    ["atomAssistConfigById", configId],
    () => getConfigById(configId),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(configId),
      // A 404 is the answer (removed, or not shared with this user).
      retry: shouldNotRetry404,
      staleTime: 1000 * 60 * 20,
      cacheTime: 1000 * 60 * 60,
      select: (data) => {
        if (!data) return null;

        // Extract model capabilities from model_version.inference_data_config
        const inferenceConfig = data?.model_version?.inference_data_config;
        const modelCapabilities =
          inferenceConfig && typeof inferenceConfig === "object"
            ? {
                supportsVision: inferenceConfig.supports_vision === true,
                supportsReasoning: inferenceConfig.supports_reasoning === true,
                supportsFunctionCalling:
                  inferenceConfig.supports_function_calling === true,
              }
            : {};

        return {
          id: data.id,
          name: data.name,
          slug: data.slug,
          description: data.description,
          initialPrompt: data.initial_prompt,
          systemPrompt: data.system_prompt,
          modelId: data.config_data?.model_id,
          modelVersionNum: data.model_version?.version_number,
          topSearchResults: data.config_data?.top_search_results,
          // Persona-attached datasets — needed so callers (e.g. ERChatPanel)
          // can populate datasetsForSearchContext, which round-trips into
          // dialogue.config['dataset_ids'] on creation. Without this the
          // agent_service per-turn diff misfires from turn 2 onward.
          datasets: data.datasets,
          chatConfigId: data.id,
          modelCapabilities,
          visibilityLevel: data.visibility_level,
          myAccess: data.my_access,
        };
      },
    },
  );
};

const createConfig = (configData) => {
  return axios
    .post(`${nuclearn_api_url}/chat/config`, configData)
    .then((response) => {
      return response.data;
    });
};

export const useCreateConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configData) => {
      return createConfig(configData);
    },
    onSuccess: (configData) => {
      queryClient.invalidateQueries(["atomAssistConfig"]);
      queryClient.invalidateQueries(["atomAssistConfig", configData.slug]);
      queryClient.invalidateQueries(["atomAssistConfigById"]);
      queryClient.invalidateQueries(["trendingPersonas"]);
    },
  });
};

const updateConfig = (slugName, configData) => {
  return axios
    .put(`${nuclearn_api_url}/chat/config/${slugName}`, configData)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateConfig = (slugName) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configData) => {
      return updateConfig(slugName, configData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["atomAssistConfig"]);
      queryClient.invalidateQueries(["atomAssistConfig", slugName]);
      queryClient.invalidateQueries(["atomAssistConfigById"]);
      queryClient.invalidateQueries(["trendingPersonas"]);
    },
  });
};

const deleteConfigById = (slug) => {
  return axios
    .delete(`${nuclearn_api_url}/chat/config/${slug}`)
    .then((response) => {
      return response.data;
    });
};

export const useDeleteConfigById = ({ options } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug) => {
      return deleteConfigById(slug);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["atomAssistConfig"]);
      queryClient.invalidateQueries(["trendingPersonas"]);
      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: options?.onError,
  });
};

const getAllAtomAssistTags = () => {
  return axios.get(`${nuclearn_api_url}/chat/tags`).then((response) => {
    return response.data;
  });
};

export const useGetAllAtomAssistTags = () => {
  return useQuery(["atomAssistTags"], () => getAllAtomAssistTags(), {
    refetchOnWindowFocus: false,
  });
};

const getAllToolkits = () => {
  return axios.get(`${nuclearn_api_url}/tools/toolkits`).then((response) => {
    return response.data;
  });
};

export const useGetAllToolkits = () => {
  return useQuery(["toolkits"], () => getAllToolkits(), {
    refetchOnWindowFocus: false,
  });
};

// Persona starring functions
export const starPersona = (personaId) => {
  return axios
    .post(`${nuclearn_api_url}/chat/config/${personaId}/star`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const unstarPersona = (personaId) => {
  return axios
    .delete(`${nuclearn_api_url}/chat/config/${personaId}/star`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const getPersonaStarredByUsers = (personaId) => {
  return axios
    .get(`${nuclearn_api_url}/chat/config/${personaId}/starred-by`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const useGetPersonaStarredByUsers = (personaId, enabled = false) => {
  return useQuery(
    ["personaStarredBy", personaId],
    () => getPersonaStarredByUsers(personaId),
    {
      enabled: enabled && !!personaId,
      refetchOnWindowFocus: false,
    },
  );
};

export const getTrendingPersonas = ({ days = 7, limit = 10 }) => {
  return axios
    .get(`${nuclearn_api_url}/chat/config/trending`, {
      params: { days, limit },
    })
    .then((response) => ensureArray(response.data, "GET /chat/config/trending"))
    .catch((error) => {
      throw error;
    });
};

const useGetTrendingPersonas = ({ days = 7, limit = 10 }) => {
  return useQuery(
    ["trendingPersonas", days, limit],
    () => getTrendingPersonas({ days, limit }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );
};

export const useStarPersona = () => {
  const queryClient = useQueryClient();
  return useMutation(starPersona, {
    onMutate: async (personaId) => {
      // Cancel any outgoing refetches for all related queries
      await queryClient.cancelQueries({ queryKey: ["atomAssistConfig"] });
      await queryClient.cancelQueries({ queryKey: ["trendingPersonas"] });

      // Get all queries that start with atomAssistConfig
      const atomAssistQueries = queryClient.getQueriesData({
        queryKey: ["atomAssistConfig"],
      });
      const trendingQueries = queryClient.getQueriesData({
        queryKey: ["trendingPersonas"],
      });

      // Combine all previous data for rollback
      const previousData = [...atomAssistQueries, ...trendingQueries];

      // Update function for persona data
      const updatePersona = (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((persona) =>
          persona.id === personaId
            ? {
                ...persona,
                is_starred_by_user: true,
                star_count: persona.star_count + 1,
              }
            : persona,
        );
      };

      // Optimistically update all matching atomAssistConfig queries
      queryClient.setQueriesData(
        { queryKey: ["atomAssistConfig"] },
        updatePersona,
      );

      // Also update trending personas queries
      queryClient.setQueriesData(
        { queryKey: ["trendingPersonas"] },
        updatePersona,
      );

      // Return context with all previous data
      return { previousData };
    },
    onError: (err, personaId, context) => {
      // Roll back all queries to their previous state
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, personaId) => {
      // Update function with server response
      const updateWithServerData = (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((persona) =>
          persona.id === personaId
            ? {
                ...persona,
                is_starred_by_user: data.is_starred,
                star_count: data.star_count,
              }
            : persona,
        );
      };

      // Update all atomAssistConfig queries with server response
      queryClient.setQueriesData(
        { queryKey: ["atomAssistConfig"] },
        updateWithServerData,
      );

      // Also update trending personas queries
      queryClient.setQueriesData(
        { queryKey: ["trendingPersonas"] },
        updateWithServerData,
      );

      // Invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ["atomAssistConfig"] });
      queryClient.invalidateQueries({ queryKey: ["trendingPersonas"] });
    },
  });
};

export const useUnstarPersona = () => {
  const queryClient = useQueryClient();
  return useMutation(unstarPersona, {
    onMutate: async (personaId) => {
      // Cancel any outgoing refetches for all related queries
      await queryClient.cancelQueries({ queryKey: ["atomAssistConfig"] });
      await queryClient.cancelQueries({ queryKey: ["trendingPersonas"] });

      // Get all queries that start with atomAssistConfig
      const atomAssistQueries = queryClient.getQueriesData({
        queryKey: ["atomAssistConfig"],
      });
      const trendingQueries = queryClient.getQueriesData({
        queryKey: ["trendingPersonas"],
      });

      // Combine all previous data for rollback
      const previousData = [...atomAssistQueries, ...trendingQueries];

      // Update function for persona data
      const updatePersona = (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((persona) =>
          persona.id === personaId
            ? {
                ...persona,
                is_starred_by_user: false,
                star_count: Math.max(0, persona.star_count - 1),
              }
            : persona,
        );
      };

      // Optimistically update all matching atomAssistConfig queries
      queryClient.setQueriesData(
        { queryKey: ["atomAssistConfig"] },
        updatePersona,
      );

      // Also update trending personas queries
      queryClient.setQueriesData(
        { queryKey: ["trendingPersonas"] },
        updatePersona,
      );

      // Return context with all previous data
      return { previousData };
    },
    onError: (err, personaId, context) => {
      // Roll back all queries to their previous state
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, personaId) => {
      // Update function with server response
      const updateWithServerData = (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((persona) =>
          persona.id === personaId
            ? {
                ...persona,
                is_starred_by_user: data.is_starred,
                star_count: data.star_count,
              }
            : persona,
        );
      };

      // Update all atomAssistConfig queries with server response
      queryClient.setQueriesData(
        { queryKey: ["atomAssistConfig"] },
        updateWithServerData,
      );

      // Also update trending personas queries
      queryClient.setQueriesData(
        { queryKey: ["trendingPersonas"] },
        updateWithServerData,
      );

      // Invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ["atomAssistConfig"] });
      queryClient.invalidateQueries({ queryKey: ["trendingPersonas"] });
    },
  });
};

// ============================================================================
// Persona Wizard / LLM-Assisted Creation
// ============================================================================

/**
 * Generate persona suggestions from a plain-language description
 */
// POST /chat/config/suggestions streams Server-Sent Events (backend PR #3413).
// Read the stream and resolve with the `result` event's parsed payload; treat an
// error event, a 4xx, or a completed-but-empty stream as a rejection so the wizard
// never shows a blank "success" (issue #1977). useMutation shape is unchanged.
export const generatePersonaSuggestions = (userDescription) => {
  const baseUrl = nuclearn_api_url?.startsWith("/")
    ? `${window.location.origin}${nuclearn_api_url}`
    : nuclearn_api_url;
  const url = `${baseUrl}/chat/config/suggestions`;

  return new Promise((resolve, reject) => {
    let result = null;
    let errorMessage = null;
    let settled = false;
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };
    const finish = () =>
      settle(
        result ? resolve : reject,
        result || new Error(errorMessage || "No suggestion returned."),
      );

    const stream = fetchEventSource(url, {
      method: "POST",
      headers: {
        Authorization: authorizedUser().Authorization,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user_description: userDescription }),
      openWhenHidden: true,
      onopen: async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          // Authorization denials (#29834) return a structured detail
          // {message, code}; other error statuses may still send a string.
          const detail =
            typeof body.detail === "string"
              ? body.detail
              : body.detail?.message;
          const err = new Error(
            detail || `Request failed with status ${response.status}`,
          );
          settle(reject, err);
          throw err; // throw from onopen → fetchEventSource stops retrying
        }
      },
      onmessage: (event) => {
        if (event.event === "result") {
          result = JSON.parse(event.data);
        } else if (event.event === "error") {
          errorMessage =
            JSON.parse(event.data)?.message ||
            "Failed to generate suggestions.";
        } else if (event.event === "end") {
          finish();
        }
      },
      onclose: finish,
      onerror: (err) => {
        settle(reject, err);
        throw err; // rethrow so the one-shot stream stops instead of retrying
      },
    });

    // fetchEventSource rejects when onopen/onerror throw; we've already settled,
    // so swallow it to avoid an unhandled rejection.
    if (stream && typeof stream.catch === "function") stream.catch(() => {});
  });
};

export const useGeneratePersonaSuggestions = () => {
  return useMutation({
    mutationFn: (userDescription) =>
      generatePersonaSuggestions(userDescription),
  });
};

/**
 * Enrich an existing persona with LLM-generated metadata
 */
const enrichPersona = (personaId) => {
  return axios
    .post(`${nuclearn_api_url}/chat/config/${personaId}/enrich`)
    .then((response) => response.data);
};

const useEnrichPersona = () => {
  return useMutation({
    mutationFn: (personaId) => enrichPersona(personaId),
  });
};

/**
 * Save enrichment data to a persona
 */
const savePersonaEnrichment = (personaId, enrichmentData) => {
  return axios
    .patch(
      `${nuclearn_api_url}/chat/config/${personaId}/enrichment`,
      enrichmentData,
    )
    .then((response) => response.data);
};

const useSavePersonaEnrichment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ personaId, enrichmentData }) =>
      savePersonaEnrichment(personaId, enrichmentData),
    onSuccess: () => {
      queryClient.invalidateQueries(["atomAssistConfig"]);
      queryClient.invalidateQueries(["trendingPersonas"]);
    },
  });
};

// ============================================================================
// Default Model Capabilities
// ============================================================================

/**
 * Get the capabilities of the default model (GeneralLLM) when no persona is selected.
 * Returns supports_vision, supports_function_calling, supports_reasoning, etc.
 */
const getDefaultModelCapabilities = () => {
  return axios
    .get(`${nuclearn_api_url}/chat/default-model-capabilities`)
    .then((response) => response.data);
};

/**
 * Hook to fetch the default model capabilities.
 * Use this to determine if image pasting should be enabled when no persona is selected.
 */
export const useGetDefaultModelCapabilities = () => {
  return useQuery(
    ["defaultModelCapabilities"],
    () => getDefaultModelCapabilities(),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 10, // Cache for 10 minutes
      cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    },
  );
};
